import type { Vault } from '@/types/vault'
import { cn } from '@/utils/cn'
import { Markdown } from './markdown'
import { SeverityIcon } from './severityIcon'
import { Tooltip, TooltipContent, type TooltipProps } from './tooltip'

interface VaultFlagsTooltipProps {
  flags: Vault['flags']
}

type Props = VaultFlagsTooltipProps & Pick<TooltipProps, 'className'>

type Severity = Vault['flags'][number]['severity']
const SEVERITY_ORDER: Severity[] = ['critical', 'warning', 'info']

const SEVERITY_COLOR: Record<Severity, string> = {
  critical: 'text-destructive',
  warning: 'text-warning',
  info: 'text-accent',
}

export const VaultFlagsTooltip = ({ flags, className }: Props) => {
  if (!flags || flags.length === 0) return null

  const topSeverity = SEVERITY_ORDER.find((s) => flags.some((f) => f.severity === s)) ?? 'info'

  return (
    <Tooltip
      className={cn(
        'relative z-[1] flex min-h-6 w-fit min-w-6 items-center justify-center rounded-full border border-transparent bg-transparent',
        'hocus:border-border hocus:bg-surface',
        className
      )}
    >
      <span className={SEVERITY_COLOR[topSeverity]}>
        <SeverityIcon severity={topSeverity} size={20} />
      </span>
      <TooltipContent side="top" align="start" className="max-w-80">
        <ul className="flex flex-col gap-1">
          {flags.map((flag, index) => (
            <li key={index} className="flex items-start gap-1.5">
              <span className={cn('mt-0.5 shrink-0', SEVERITY_COLOR[flag.severity])}>
                <SeverityIcon severity={flag.severity} />
              </span>
              <Markdown className="min-w-0 break-words text-sm">{flag.content}</Markdown>
            </li>
          ))}
        </ul>
      </TooltipContent>
    </Tooltip>
  )
}
